import { ReactNode } from "react";
import { articleContent as launchKitContent } from "@/data/launchKitContent";
import { gettingStartedContent } from "@/data/docsGettingStartedContent";

export const docsContent: Record<string, ReactNode> = {
  ...gettingStartedContent,
  ...launchKitContent,
  "about-hostcaptain": (
    <div className="prose prose-slate dark:prose-invert max-w-none">
      <h2 className="text-2xl font-bold">About HostCaptain</h2>
      <p className="text-lg italic text-muted-foreground border-l-4 border-primary pl-4 my-6">
        The beginner-friendly launch pad for Turo hosts.
      </p>

      <p>
        HostCaptain gives you the knowledge, tools, and discipline to build a profitable car rental business — whether you're renting your personal car or managing a growing fleet.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">What We Do</h2>

      <p>
        Running a car-sharing business on Turo is rewarding, but also complex. Between guest disputes, protection plan decisions, financial tracking, and maintenance scheduling, hosts face dozens of decisions every week — often with incomplete information.
      </p>
      <p>
        HostCaptain consolidates everything a host needs into a single platform so you can <strong>operate with confidence instead of confusion</strong>.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Who It's For</h2>

      <div className="space-y-4 my-6">
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">🚗 Brand-New Hosts</p>
          <p className="mb-0">Deciding whether Turo is worth their time and need structured education to get started right.</p>
        </div>
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">🔑 Part-Time Hosts</p>
          <p className="mb-0">Renting their personal car and wanting to understand if they're actually making money.</p>
        </div>
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">📈 Growing Hosts</p>
          <p className="mb-0">Managing 2–8 vehicles and needing clear guidance on what to do next and which vehicle needs attention.</p>
        </div>
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">🏢 Fleet Operators</p>
          <p className="mb-0">Managing larger operations requiring attention prioritization, risk monitoring, and team collaboration.</p>
        </div>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Platform Features</h2>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Turo Launch Kit</h3>
      <p>
        A comprehensive library of <strong>educational guides</strong> covering everything from signing up as a host and listing your first car, to understanding protection plans, setting prices, and handling your first rental. Over 50 articles organized by topic — no more sifting through forums and YouTube for conflicting advice.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Expert Playbooks</h3>
      <p>
        Interactive <strong>decision trees</strong> for high-stress scenarios like damage claims, late returns, and guest disputes. Each playbook walks you through the situation step-by-step, ensuring you never miss a critical action.
      </p>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Think of it as...</p>
        <p className="mb-0">Having an experienced host guiding you through every tough moment.</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Business Dashboard</h3>
      <p>
        See your business at a glance. Monthly earnings and expense charts sit alongside per-vehicle performance cards that show <strong>revenue, expenses, net profit, and return-on-investment progress</strong> for every car in your fleet.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Earnings Tracking</h3>
      <p>
        Import your Turo trip CSV files directly into HostCaptain. The system parses trip details — guest names, dates, daily earnings, distance traveled, fees, and more — and presents summary stats including total earnings, average earnings per day, highest and lowest earning trips, and monthly earnings charts.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Expense Management</h3>
      <p>
        Track every business expense — maintenance, insurance, cleaning, loan payments, registration fees, and more. Assign expenses to specific vehicles or mark them as <strong>"Operations"</strong> for company-wide costs like LLC registration or office supplies.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Vehicle Fleet Management</h3>
      <p>
        Add vehicles with detailed information including year, make, model, trim, VIN, license plate, Turo vehicle ID, purchase price, mileage, and photos. Track each vehicle's earnings and expenses to see <strong>true profitability</strong>. Merge duplicate vehicle records and manage your fleet as it grows.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Maintenance Tracking</h3>
      <p>
        Schedule and track maintenance tasks per vehicle — oil changes, tire rotations, inspections, and more. Set due dates and mileage thresholds to stay on top of upkeep and avoid costly breakdowns.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Team Collaboration</h3>
      <p>
        Invite team members to your company account. Owners can manage members and share access to the business data — ideal for hosts who work with partners, cleaners, or operations staff.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Pricing</h2>

      <p>HostCaptain offers four tiers designed to match your hosting stage:</p>

      <div className="space-y-4 my-6">
        <div className="bg-muted/50 rounded-lg p-4">
          <p className="font-semibold mb-1">Free — Get Oriented</p>
          <p className="mb-0 text-sm text-muted-foreground">Add vehicles, log earnings and expenses, and evaluate early profitability. Includes the Turo Launch Kit basics and orientation dashboard.</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-4">
          <p className="font-semibold mb-1">Pro ($29/month) — Know What to Do Next</p>
          <p className="mb-0 text-sm text-muted-foreground">Get recommended actions, underperforming vehicle detection, vehicle comparisons, and clear explanations of why things matter.</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-4">
          <p className="font-semibold mb-1">Scale ($79/month) — Focus Where It Matters</p>
          <p className="mb-0 text-sm text-muted-foreground">Attention ranking across your fleet, impact estimates, and fleet-level performance overview.</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-4">
          <p className="font-semibold mb-1">Fleet (Custom) — Avoid Risk and Chaos</p>
          <p className="mb-0 text-sm text-muted-foreground">Exception monitoring, risk-focused alerts, historical pattern warnings, and team access with permissions.</p>
        </div>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Our Mission</h2>

      <div className="bg-primary/5 border-2 border-primary/30 rounded-xl p-6 my-6">
        <p className="text-xl font-semibold text-center mb-4">The ultimate co-pilot for Turo hosts ⚓</p>
        <p className="text-center mb-0">
          Running a car rental business can be complex, but it's possible to be profitable and successful with the right skillset and disciplined operations. HostCaptain exists to give every host — from day one to fleet scale — the <strong>knowledge and tools to succeed</strong>.
        </p>
      </div>
    </div>
  ),

  "our-approach": (
    <div className="prose prose-slate dark:prose-invert max-w-none">
      <h2 className="text-2xl font-bold">Our Approach</h2>
      <p className="text-lg italic text-muted-foreground border-l-4 border-primary pl-4 my-6">
        Turo hosts have been underserved by the tools available to them. HostCaptain was built to change that.
      </p>

      <p>
        We address the real gaps in how hosts <strong>learn, operate, and grow</strong> their businesses.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">The Problem Hosts Face Today</h2>

      <p>
        When a new host signs up for Turo, they're immediately confronted with questions that have real financial consequences: Which protection plan should I choose? How do I price my car? What happens when a guest returns the car with damage? How do I know if I'm actually making money?
      </p>
      <p>
        Today, hosts cobble together answers from a patchwork of sources — each with significant limitations:
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Social Media Groups and Forums</h3>
      <p>
        Facebook groups, Reddit threads, and YouTube channels are the first place most new hosts turn. The problem? You get <strong>ten different answers to the same question</strong>. Advice is anecdotal, often contradictory, and sometimes dangerously wrong. There's no way to verify which response is accurate, and answers become outdated as Turo changes its policies.
      </p>
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ The Risk</p>
        <p className="mb-0">For time-sensitive situations — like a guest returning a damaged car — scrolling through forum posts is the last thing you should be doing.</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Spreadsheet Templates</h3>
      <p>
        Many hosts turn to Excel or Google Sheets templates to track finances. While better than nothing, spreadsheets require manual data entry, break easily, and never tell you <em>what to do</em> with the numbers. You might calculate that a vehicle earned $1,200 last month, but a spreadsheet won't flag that the same car <strong>cost $1,400 in expenses</strong>.
      </p>
      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Key Insight</p>
        <p className="mb-0">Spreadsheets show data — they don't surface insight.</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Fleet Management Software Built for Other Industries</h3>
      <p>
        Some hosts try to adopt fleet management tools designed for traditional rental car companies, logistics firms, or corporate vehicle pools. These platforms are powerful, but they're built for operations with dedicated staff, corporate accounts, and hundreds of vehicles.
      </p>
      <p>
        For an individual host with 1–10 cars, they're overwhelmingly complex, prohibitively expensive, and full of features that have nothing to do with the Turo hosting experience. They don't understand Turo's CSV format, Turo's protection plans, or the unique challenges of peer-to-peer car sharing.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Turo's Own Tools</h3>
      <p>
        Turo provides a basic host dashboard with earnings summaries and trip history. But it's designed to facilitate transactions, not to help you run a business. There's no expense tracking, no per-vehicle P&L, no guidance on what to do when things go wrong, and no educational resources to help new hosts avoid common mistakes.
      </p>
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ The Gap</p>
        <p className="mb-0">Turo gives you the revenue number — it doesn't tell you whether you're profitable.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">How HostCaptain Is Different</h2>

      <div className="space-y-4 my-6">
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">🎯 Built Specifically for Turo Hosts</p>
          <p className="mb-0">Every feature was designed for the Turo hosting workflow. Our CSV import understands Turo's exact export format — reservation IDs, trip fees, guest names, distance traveled, all of it. Our expense tracking includes categories that matter to hosts: cleaning supplies, parking, tolls, commercial insurance, and off-platform costs. We speak your language because we <em>are</em> hosts.</p>
        </div>
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">📚 Education First, Not Just Data</p>
          <p className="mb-0">Most tools give you charts and tables and leave you to figure out what they mean. HostCaptain starts with education. Our Turo Launch Kit provides over 50 structured guides. When you face a stressful situation — a damage claim, a late return, a guest dispute — our Expert Playbooks walk you through it step-by-step instead of leaving you to Google for answers.</p>
        </div>
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">💡 Actionable Insight, Not Just Numbers</p>
          <p className="mb-0">HostCaptain doesn't just show you your revenue — it shows you your <em>true profit</em> per vehicle after expenses. It doesn't just list your cars — it flags which ones are underperforming and why. As you grow, the platform surfaces the decisions that matter most.</p>
        </div>
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">📈 Grows With You</p>
          <p className="mb-0">Our tiered approach means you only pay for what you need. A brand-new host uses the Free plan to get oriented. A growing host upgrades to Pro for decision support. A fleet operator moves to Scale for attention prioritization. At each stage, HostCaptain surfaces the right level of complexity — never more, never less.</p>
        </div>
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">🔗 Replaces the Patchwork</p>
          <p className="mb-0">Instead of juggling forum advice, spreadsheet templates, and mismatched software, HostCaptain provides a single platform where you can learn, track, analyze, and act — all in one place.</p>
        </div>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Our Philosophy</h2>

      <div className="bg-primary/5 border-2 border-primary/30 rounded-xl p-6 my-6">
        <p className="text-xl font-semibold text-center mb-4">Skill + Discipline = Success ⚓</p>
        <p className="text-center mb-0">
          We believe that running a successful Turo business isn't about luck or market timing — it's about building the right skillset and applying discipline to your operations. HostCaptain exists to accelerate that process: giving you the <strong>knowledge to make smart decisions</strong> from day one, and the <strong>tools to execute them</strong> as you grow.
        </p>
      </div>

      <p>
        We're not building another generic dashboard. We're building the co-pilot that every Turo host deserves — one that understands the specific challenges of peer-to-peer car sharing and is designed to help you navigate them with confidence.
      </p>
    </div>
  ),
};
